import type { EmailKind, UnavailableReason, UserStatus } from '../api/types';

/** Initials for an avatar tile, at most two letters. */
export function initials(displayName: string, email: string): string {
  const source = displayName.trim() || email.trim();
  const parts = source.split(/[\s@._-]+/).filter(Boolean);
  if (parts.length === 0) return '?';
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
}

const STATUS_LABELS: Record<UserStatus, string> = {
  invited: 'Invited',
  pending_approval: 'Awaiting review',
  active: 'Active',
  rejected: 'Declined',
  suspended: 'Suspended',
  archived: 'Archived',
};

export function statusLabel(status: UserStatus): string {
  return STATUS_LABELS[status];
}

const EMAIL_KIND_LABELS: Record<EmailKind, string> = {
  corporate: 'Company address',
  free: 'Personal address',
  disposable: 'Temporary address',
};

/** What the approval decision turned on, in words. */
export function emailKindLabel(kind: EmailKind): string {
  return EMAIL_KIND_LABELS[kind];
}

const UNAVAILABLE_LABELS: Record<UnavailableReason, string> = {
  not_granted: 'No access',
  coming_soon: 'Coming soon',
  maintenance: 'Maintenance',
  not_configured: 'Not configured',
};

/** Short label for a tool card's status pill. */
export function unavailableLabel(reason: UnavailableReason | undefined): string {
  return reason ? UNAVAILABLE_LABELS[reason] : 'Active';
}

/** A sentence explaining a non-launchable tool, for the tool page. */
export function unavailableExplanation(reason: UnavailableReason | undefined): string {
  switch (reason) {
    case 'not_granted':
      return 'Your account does not have access to this tool. Contact EJOT if you believe it should.';
    case 'coming_soon':
      return 'This tool has not been released yet. It will appear here when it is available.';
    case 'maintenance':
      return 'This tool is temporarily unavailable while maintenance is in progress.';
    case 'not_configured':
      return 'This tool has no address configured for this environment. Contact EJOT.';
    default:
      return '';
  }
}

/** "2 hours ago" / "Yesterday" / a date, from an ISO timestamp. */
export function relativeTime(iso: string | undefined, now = new Date()): string {
  if (!iso) return '—';
  const then = new Date(iso);
  if (Number.isNaN(then.getTime())) return '—';

  const seconds = Math.round((now.getTime() - then.getTime()) / 1000);
  if (seconds < 60) return 'Just now';
  const minutes = Math.round(seconds / 60);
  if (minutes < 60) return `${minutes} min ago`;
  const hours = Math.round(minutes / 60);
  if (hours < 24) return `${hours} ${hours === 1 ? 'hour' : 'hours'} ago`;
  const days = Math.round(hours / 24);
  if (days === 1) return 'Yesterday';
  if (days < 7) return `${days} days ago`;
  if (days < 30) return `${Math.round(days / 7)} weeks ago`;
  return then.toLocaleDateString();
}

/** Absolute date for tooltips and detail rows. */
export function absoluteDate(iso: string | undefined): string {
  if (!iso) return '—';
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return '—';
  return date.toLocaleString();
}
